import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label as UILabel } from "@/components/ui/label";
import {
  BookOpen, Plus, Star, StarOff, Search, Clock, Users, FolderTree, FileText,
  Trash2, Archive, LayoutTemplate, Home, RotateCcw, Share2,
} from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { v: "general",        l: "General" },
  { v: "project_doc",    l: "Project Doc" },
  { v: "technical_doc",  l: "Technical" },
  { v: "requirement",    l: "Requirement" },
  { v: "sop",            l: "SOP" },
  { v: "meeting_notes",  l: "Meeting Notes" },
  { v: "decision",       l: "Decision" },
  { v: "release_notes",  l: "Release Notes" },
  { v: "cr_doc",         l: "CR Doc" },
  { v: "knowledge_base", l: "Knowledge Base" },
  { v: "policy",         l: "Policy" },
];
const typeLabel = (v) => DOC_TYPES.find((t) => t.v === v)?.l || "General";
const initialsOf = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
const timeAgo = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = Math.floor(diff / 86400);
  if (d < 30) return `${d}d ago`;
  return iso.slice(0, 10);
};

const TABS = [
  { v: "home",      l: "Home",            icon: Home },
  { v: "mine",      l: "My Documents",    icon: FileText },
  { v: "recent",    l: "Recent",          icon: Clock },
  { v: "favorites", l: "Favorites",       icon: Star },
  { v: "shared",    l: "Shared With Me",  icon: Share2 },
  { v: "collections", l: "Collections",   icon: FolderTree },
  { v: "templates", l: "Templates",       icon: LayoutTemplate },
  { v: "trash",     l: "Trash",           icon: Trash2 },
];

function DocRow({ d, employees, onFavorite, onRestore, onPurge, onTrash, canEdit, nav }) {
  const owner = employees.find((e) => e.user_id === d.owner_id || e.id === d.owner_id);
  const isTrashed = !!d.trashed_at;
  return (
    <div className="glass rounded-lg p-3 flex items-center gap-3" data-testid={`doc-row-${d.id}`}>
      <div className="h-9 w-9 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center text-base">
        {d.icon || "📄"}
      </div>
      <div className="flex-1 min-w-0">
        <button
          onClick={() => nav(`/documentation/${d.id}`)}
          className="font-medium text-sm hover:text-primary text-left truncate block w-full"
        >
          {d.title || "Untitled"}
        </button>
        <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-muted-foreground">
          <Badge variant="outline" className="text-[9px]">{typeLabel(d.doc_type)}</Badge>
          {d.child_count > 0 && <span>· {d.child_count} nested</span>}
          <span>· updated {timeAgo(d.updated_at)}</span>
          {d.status && d.status !== "draft" && <Badge variant="outline" className="text-[9px] capitalize">{d.status.replace("_", " ")}</Badge>}
        </div>
      </div>
      {owner && (
        <div className="flex items-center gap-1 text-[10px] shrink-0" title={owner.full_name}>
          <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px]">{initialsOf(owner.full_name)}</AvatarFallback></Avatar>
          <span className="hidden md:inline">{owner.full_name}</span>
        </div>
      )}
      {!isTrashed && (
        <Button
          size="icon" variant="ghost"
          data-testid={`doc-fav-${d.id}`}
          onClick={(e) => { e.stopPropagation(); onFavorite(d); }}
          title={d.is_favorite ? "Unfavorite" : "Favorite"}
          className="h-7 w-7"
        >
          {d.is_favorite
            ? <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            : <StarOff className="w-4 h-4" />}
        </Button>
      )}
      {canEdit && !isTrashed && (
        <Button size="icon" variant="ghost" onClick={() => onTrash(d)} className="h-7 w-7" data-testid={`doc-trash-${d.id}`}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      )}
      {canEdit && isTrashed && (
        <>
          <Button size="icon" variant="ghost" onClick={() => onRestore(d)} className="h-7 w-7" title="Restore" data-testid={`doc-restore-${d.id}`}>
            <RotateCcw className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" onClick={() => onPurge(d)} className="h-7 w-7" title="Delete forever" data-testid={`doc-purge-${d.id}`}>
            <Archive className="w-4 h-4 text-red-500" />
          </Button>
        </>
      )}
    </div>
  );
}

function NewDocDialog({ projectId }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("general");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/documents?tab=templates").then((r) => setTemplates(r.data));
    setTitle(""); setDocType("general"); setTemplateId("");
  }, [open]);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { title: title.trim() || "Untitled", doc_type: docType };
      if (templateId) payload.template_id = templateId;
      if (projectId) payload.project_id = projectId;
      const { data } = await api.post("/documents", payload);
      setOpen(false);
      toast.success("Document created");
      nav(`/documentation/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="new-doc-btn">
        <Plus className="w-4 h-4 mr-1" />New Document
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-doc-dialog">
          <DialogHeader>
            <DialogTitle>New Document</DialogTitle>
            <DialogDescription>Pick a template to pre-fill common sections, or start blank.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <UILabel>Title</UILabel>
              <Input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Untitled" data-testid="new-doc-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <UILabel>Type</UILabel>
                <Select value={docType} onValueChange={setDocType}>
                  <SelectTrigger data-testid="new-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {DOC_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <UILabel>Template (optional)</UILabel>
                <Select value={templateId || "blank"} onValueChange={(v) => setTemplateId(v === "blank" ? "" : v)}>
                  <SelectTrigger data-testid="new-doc-template"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="blank">Blank</SelectItem>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={create} disabled={saving} data-testid="new-doc-create">
              {saving ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Documentation() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "home");
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ total: 0, mine: 0, favorites: 0, trashed: 0 });

  const reload = async () => {
    const [docRes, statsRes] = await Promise.all([
      api.get(`/documents?tab=${tab}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
      api.get("/documents/stats"),
    ]);
    setDocs(docRes.data); setStats(statsRes.data);
  };
  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data));
  }, []);
  useEffect(() => {
    reload();
    setParams({ tab }, { replace: true });
    // eslint-disable-next-line
  }, [tab]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => reload(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const toggleFav = async (d) => {
    try {
      if (d.is_favorite) await api.delete(`/documents/${d.id}/favorite`);
      else await api.post(`/documents/${d.id}/favorite`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const trash = async (d) => {
    if (!window.confirm(`Move "${d.title}" to Trash?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Moved to Trash"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const restore = async (d) => {
    try { await api.post(`/documents/${d.id}/restore`); toast.success("Restored"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const purge = async (d) => {
    if (!window.confirm(`Delete "${d.title}" FOREVER? This cannot be undone.`)) return;
    try { await api.delete(`/documents/${d.id}/permanent`); toast.success("Deleted forever"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const kpis = [
    { l: "All Documents", v: stats.total,     icon: BookOpen, color: "text-primary" },
    { l: "My Documents",  v: stats.mine,      icon: FileText, color: "text-emerald-500" },
    { l: "Favorites",     v: stats.favorites, icon: Star,     color: "text-amber-500" },
    { l: "In Trash",      v: stats.trashed,   icon: Trash2,   color: "text-red-500" },
  ];

  // Collections = top-level documents (grouping buckets).
  const collections = useMemo(() => docs.filter((d) => !d.parent_document_id), [docs]);
  const sharedCount = useMemo(() => docs.filter((d) => d.owner_id !== user?.id).length, [docs, user]);

  return (
    <div className="space-y-6">
      <PageHeader
        testid="docs-header"
        title="Documentation"
        subtitle="Your workspace knowledge — docs linked to Projects, CRs, Tasks and Releases"
        actions={<NewDocDialog />}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="docs-kpis">
        {kpis.map(({ l, v, icon: Icon, color }) => (
          <div key={l} className="glass rounded-xl p-4 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg bg-background/50 flex items-center justify-center ${color}`}><Icon className="w-5 h-5" /></div>
            <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div><div className="number font-medium text-xl leading-none mt-1">{v}</div></div>
          </div>
        ))}
      </div>

      <SectionCard
        title={TABS.find((t) => t.v === tab)?.l || "Home"}
        subtitle={
          tab === "home"       ? "Top-level documents across the workspace" :
          tab === "mine"       ? "Documents you own" :
          tab === "recent"     ? "Most recently updated — last 20" :
          tab === "favorites"  ? "Starred for quick access" :
          tab === "shared"     ? `${sharedCount} doc${sharedCount === 1 ? "" : "s"} authored by others, visible to you` :
          tab === "collections"? "Top-level docs act as collection buckets — nest children inside them" :
          tab === "templates"  ? "Reusable starting points for new documents" :
          /* trash */            "Restore within 30 days or delete forever"
        }
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
              <Input className="pl-9 w-64" placeholder="Search documents…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="docs-search" />
            </div>
          </div>
        }
      >
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="flex flex-wrap h-auto mb-4 w-full justify-start gap-1 bg-transparent p-0">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.v} value={t.v}
                data-testid={`docs-tab-${t.v}`}
                className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:border-primary/30 border border-transparent rounded-full px-3 py-1.5 text-xs gap-1.5"
              >
                <t.icon className="w-3.5 h-3.5" />{t.l}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {tab === "collections" ? (
          collections.length === 0
            ? <div className="text-sm text-muted-foreground py-10 text-center">No collections yet. Create a top-level document to start one.</div>
            : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="docs-collections">
                {collections.map((d) => (
                  <RouterLink key={d.id} to={`/documentation/${d.id}`} className="glass rounded-xl p-4 hover:border-primary/40 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-lg">{d.icon || "📁"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{d.title}</div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                          {d.child_count || 0} nested · updated {timeAgo(d.updated_at)}
                        </div>
                      </div>
                    </div>
                  </RouterLink>
                ))}
              </div>
            )
        ) : docs.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center" data-testid="docs-empty">
            {tab === "trash" ? "Nothing in trash." :
             tab === "templates" ? "No templates yet." :
             tab === "favorites" ? "Star any doc to pin it here." :
             "No documents here yet. Click \"New Document\" to get started."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="docs-list">
            {docs.map((d) => (
              <DocRow
                key={d.id}
                d={d}
                employees={employees}
                nav={nav}
                canEdit={user?.role === "admin" || d.owner_id === user?.id}
                onFavorite={toggleFav}
                onTrash={trash}
                onRestore={restore}
                onPurge={purge}
              />
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
